import {
  CasheirPaymentsMethod,
  cashierDeleveryType,
  CashierPostOrder,
  FilteredCartType,
} from "@/types/CashierProductType";
import CashierPaymentCard from "./CashierPaymentCard";
import { IoMdClose } from "react-icons/io";
import { useState, useEffect } from "react";
import { Order } from "@/types/ApiTypes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import toast from "react-hot-toast";
import axios from "axios";
import { clearCart } from "@/store/cart.slice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/utils/AppConfig";
import CashierTableList from "./CashierTableList";
import { StandaloneTable } from "@/types/globalTypes";
import { formatMoneyForInput } from "@/utils/helpers";
import { useUserRole } from "@/hooks/useUserRole";

const paymentsCards: { type: CasheirPaymentsMethod }[] = [
  { type: "cash" },
  { type: "card" },
  { type: "cash-card" },
  { type: "pay-later" },
];

export type cashierModalFormType = {
  cashPaid: undefined | number;
  networkPaid: undefined | number;
  type: cashierDeleveryType;
};

type CashierPaymentModalProps = {
  setIsPaymentModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  FilteredCart: FilteredCartType;
  couponInputValue: string;
  CopunSuccessMsg: string | null;
  setOrder?: React.Dispatch<React.SetStateAction<Order | null>>;
  isValidPhoneNumber: boolean;
  totalPrice: number;
};

function CashierPaymentModal({
  setIsPaymentModalOpened,
  FilteredCart,
  couponInputValue,
  CopunSuccessMsg,
  setOrder,
  isValidPhoneNumber,
  totalPrice,
}: CashierPaymentModalProps) {
  const {isCashier,isWaiter,openAppReviewBasedOnRole} = useUserRole()
  const [isRequesting, setIsRequesting] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const customerData = useSelector((state: RootState) => state.cart.customer);
  const [paymentType, setPaymentType] = useState<CasheirPaymentsMethod | null>(
    null,
  );

  const selectedBranch = useSelector((state: RootState) => state.branch.branch);
  const customerdata = useSelector((state: RootState) => state.cart.customer);
  const dispatch = useDispatch();
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const [selectedTable, setselectedTable] = useState<StandaloneTable | null>(null);


  const deleveryTypeOptions = [
    { value: "pickup", label: t("cashierPaymentMethod.pickup") },
    { value: "internal", label: t("cashierPaymentMethod.insideRestaurant") },
    // { value: "delivery", label: t("cashierPaymentMethod.delivery") },
  ];

  const [isCashInputFoucused, setIsCashInputFoucused] = useState(false);
  const [isSecondCashInputFoucused, setIsSecondCashInputFoucused] = useState(false);

  const getValidationSchema = (
    paymentType: CasheirPaymentsMethod | null,
    totalPrice: number,
  ) => {
    if (!paymentType) return Yup.object({});

    if (paymentType === "cash") {
      return Yup.object({
        cashPaid: Yup.number()
          .typeError(t("cashierPaymentMethod.mustBeNumber") as string)
          .required(t("cashierPaymentMethod.cashAmountRequired") as string)
          .positive(t("cashierPaymentMethod.cashMustBePositive") as string)
          .min(
            totalPrice,
            t("cashierPaymentMethod.cashGreaterThanTotal") as string,
          ),
      });
    }

    if (paymentType === "cash-card") {
      return Yup.object({
        cashPaid: Yup.number()
          .typeError(t("cashierPaymentMethod.mustBeNumber") as string)
          .required(t("cashierPaymentMethod.cashAmountRequired") as string)
          .positive(t("cashierPaymentMethod.cashMustBePositive") as string)
          .test(
            "lessThanTotal",
            t("cashierPaymentMethod.cashLessThanTotal") as string,
            (value) => (value ? value < totalPrice : false),
          ),
        networkPaid: Yup.number()
          .typeError(t("cashierPaymentMethod.mustBeNumber") as string)
          .required(t("cashierPaymentMethod.networkAmountRequired") as string)
          .positive(t("cashierPaymentMethod.networkMustBePositive") as string)
          .test(
            "validNetworkAmount",
            t("cashierPaymentMethod.cashAndNetworkEqualTotal") as string,
            (value, context) => {
              const cashPaid = context.parent.cashPaid || 0;
              return Math.abs(cashPaid + (value || 0) - totalPrice) < 0.01;
            },
          ),
        type: Yup.string()
          .oneOf(["pickup", "delivery"])
          .required(t("cashierPaymentMethod.deleveryTypeRequired") as string),
      });
    }

    return Yup.object({});
  };

  const initialValues: cashierModalFormType = {
    cashPaid: undefined,
    networkPaid: undefined,
    type: "pickup", // or "delivery" or "internal"
  };

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema(paymentType, totalPrice),
    onSubmit: () => handleSubmitOrder(),
    enableReinitialize: true,
    validateOnChange: true, // Ensure validation happens on every change
    validateOnBlur: true,
  });

  useEffect(() => {
    // Validate form whenever paymentType or values change
    if (paymentType) {
      formik.validateForm();
    }
    // eslint-disable-next-line
  }, [paymentType, formik.values]);

  const handleSubmitOrder = async () => {
    if (!paymentType) {
      toast.error(t("cashierPaymentMethod.selectPaymentMethod") as string);
      return;
    }

    if (paymentType === "pay-later" && !selectedTable) {
      return toast.error(t("cashierPaymentMethod.selectTable", { duration: 4000 }) as string);
    }

    const loadingToast = toast.loading(
      t("cashierPaymentMethod.placingOrder") as string,
    );
    setIsRequesting(true);

    const order: CashierPostOrder = {
      branch: selectedBranch?._id || "",
      items: FilteredCart,
      paymentData: {
        type: paymentType,
        cashPaid: paymentType === "card" ? undefined : formik.values.cashPaid,
        networkPaid:
          paymentType === "cash-card" ? formik.values.networkPaid : undefined,
      },
      coupon: CopunSuccessMsg ? couponInputValue : undefined,
      customer:
        customerdata.name || customerdata.phone
          ? {
            name: customerdata.name || undefined,
            phone: customerdata.phone || undefined,
          }
          : undefined,
      type: formik.values.type,
      dineIn: formik.values.type === "internal" && selectedTable
        ? {
          floorId: selectedTable.floorId,
          tableId: selectedTable._id,
        }
        : undefined,
    };

    const makeOrderUrl = `${BASE_URL}/api/dashboard/cashier/make-order?locale=${lang}`;
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const res = await axios.post(makeOrderUrl, order, { headers });

      if (res.data.success) {
        toast.success(
          res.data.message ||
          (t("cashierPaymentMethod.orderSuccess") as string),
        );
        // set order leads to auto print the order 
        if (paymentType !== "pay-later" && setOrder) {
          setOrder(res.data.order);
        }
        dispatch(clearCart());
        setIsPaymentModalOpened(false);
        if(isWaiter||isCashier){
          openAppReviewBasedOnRole()
        }
      } else {
        const message =
          res.data.error || (t("cashierPaymentMethod.orderFailed") as string);
        toast.error(message);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          (t("cashierPaymentMethod.orderFailed") as string);
        toast.error(message);
      } else {
        toast.error(t("cashierPaymentMethod.orderFailed") as string);
      }
    } finally {
      setIsRequesting(false);
      toast.dismiss(loadingToast);
    }
  };

  const handleCashPaidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value === "" ? undefined : Number(value);

    // Update cashPaid field and mark it as touched
    formik.setFieldValue("cashPaid", numericValue);
    formik.setFieldTouched("cashPaid", true, false);

    // For cash-card payment type, automatically update networkPaid
    if (paymentType === "cash-card") {
      if (numericValue === undefined) {
        formik.setFieldValue("networkPaid", undefined);
      } else if (numericValue >= totalPrice) {
        formik.setFieldValue("networkPaid", 0);
      } else if (numericValue < 0) {
        formik.setFieldValue("networkPaid", totalPrice);
      } else {
        formik.setFieldValue("networkPaid", totalPrice - numericValue);
      }
      // Also mark networkPaid as touched
      formik.setFieldTouched("networkPaid", true, false);
    }

    // Validate immediately after change
    formik.validateForm();
  };

  const handleNetworkPaidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value === "" ? undefined : Number(value);
    formik.setFieldValue("networkPaid", numericValue);
    formik.setFieldTouched("networkPaid", true, false);
    formik.validateForm();
  };

  if (!FilteredCart.length || !isValidPhoneNumber) return null;

  return (
    <div className="fixed inset-0 -top-10 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative flex max-h-[85vh] min-w-[300px] max-w-[252px] flex-col gap-4 overflow-y-auto rounded-lg bg-white px-6 pb-5 pt-10 sm:max-w-[90%]">
        <div className="relative flex w-full flex-col items-center gap-x-5 gap-y-6 sm:flex-row">
          {paymentsCards.map((card) => (
            <CashierPaymentCard
              key={card.type}
              name={t(`paymentMethods.${card.type}`)}
              selectedPaymentType={card.type}
              isRequesting={isRequesting}
              paymentType={paymentType}
              deleveryType={formik.values.type}
              setPaymentType={setPaymentType}
              setFieldValue={formik.setFieldValue}
              resetForm={formik.resetForm}
            />
          ))}
        </div>

        <button
          className="absolute end-[6px] top-[6px] block text-red-500"
          onClick={() => setIsPaymentModalOpened(false)}
        >
          <IoMdClose className="text-2xl" />
        </button>

        {/* Total Paid inputs */}

        <div className="flex flex-col gap-2">
          {paymentType === "cash" && (
            <div className="flex flex-col gap-2 sm:flex-row">
              {/* Cash Paid */}
              <div className="flex w-full flex-col gap-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t("cashierPaymentMethod.cashPaid")}
                </label>

                <div className="relative h-10 w-full">
                  <input
                    type="number"
                    name="cashPaid"
                    disabled={isRequesting}
                    value={isCashInputFoucused ? formik.values.cashPaid ?? "" : formatMoneyForInput(formik.values.cashPaid ?? "")}
                    onFocus={() => setIsCashInputFoucused(true)}
                    onBlur={(e) => {
                      setIsCashInputFoucused(false);
                      formik.handleBlur(e);
                    }}
                    placeholder={t("cashierPaymentMethod.cashPaid") as string}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldTouched("cashPaid", true, false);
                    }}
                    className="h-10 w-full rounded border border-gray-300 p-2 pe-10"
                  />
                  <span className="absolute end-0 top-[50%] flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-e bg-gray-300 p-2">
                    {AppConfig.currency.icon()}
                  </span>
                </div>

                {formik.touched.cashPaid && formik.errors.cashPaid && (
                  <div className="text-sm text-red-500">
                    {formik.errors.cashPaid}
                  </div>
                )}
              </div>

              {/* the Remainder */}
              <div className="flex w-full flex-col gap-2 sm:w-fit">
                <label className="block text-sm font-medium text-gray-700">
                  {t("cashierPaymentMethod.remainder")}
                </label>

                <div className="relative h-10 w-full">
                  <input
                    type="number"
                    name="cashPaid"
                    disabled={true}
                    value={formatMoneyForInput(Math.max(
                      Number(
                        ((formik.values.cashPaid ?? 0) - totalPrice),
                      ),
                      0,
                    ))}
                    placeholder={t("cashierPaymentMethod.cashPaid") as string}
                    className="h-10 w-full rounded border border-gray-300 p-2 pe-10 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                  />
                  <span className="absolute end-0 top-[50%] flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-e bg-gray-300 p-2">
                    {AppConfig.currency.icon()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {paymentType === "cash-card" && (
            <>
              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t("cashierPaymentMethod.cashPaid")}
                </label>

                <div className="relative h-10 w-full">
                  <input
                    type="number"
                    name="cashPaid"
                    disabled={isRequesting}
                    value={isSecondCashInputFoucused ? formik.values.cashPaid ?? "" : formatMoneyForInput(formik.values.cashPaid ?? "")}
                    onFocus={() => setIsSecondCashInputFoucused(true)}
                    onBlur={(e) => {
                      setIsSecondCashInputFoucused(false);
                      formik.handleBlur(e);
                    }}
                    placeholder={t("cashierPaymentMethod.cashPaid") as string}
                    onChange={handleCashPaidChange}
                    className="h-10 w-full rounded border border-gray-300 p-2 pe-10"
                  />
                  <span className="absolute end-0 top-[50%] flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-e bg-gray-300 p-2">
                    {AppConfig.currency.icon()}
                  </span>
                </div>
                {formik.touched.cashPaid && formik.errors.cashPaid && (
                  <div className="text-sm text-red-500">
                    {formik.errors.cashPaid}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t("cashierPaymentMethod.networkPaid")}
                </label>

                <div className="relative h-10 w-full">
                  <input
                    type="number"
                    name="networkPaid"
                    disabled={true}
                    placeholder={
                      t("cashierPaymentMethod.networkPaid") as string
                    }
                    value={Number(formik.values.networkPaid).toFixed(2) ?? ""}
                    onChange={handleNetworkPaidChange}
                    onBlur={formik.handleBlur}
                    className="h-10 w-full rounded border border-gray-300 p-2 pe-10 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                  />

                  <span className="absolute end-0 top-[50%] flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-e bg-gray-300 p-2">
                    {AppConfig.currency.icon()}
                  </span>
                </div>

                {formik.touched.networkPaid && formik.errors.networkPaid && (
                  <div className="text-sm text-red-500">
                    {formik.errors.networkPaid}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* order Type Select */}

        <div className="mt-[-10px] flex w-full flex-col gap-1 rounded-md">
          <label htmlFor="deleveryType">
            {t("cashierPaymentMethod.deleveryType")}
          </label>
          <select
            name="deleveryType"
            id="deleveryType"
            onChange={(e) => {
              if (paymentType === "pay-later") {
                formik.setFieldValue("paymentType", null);
              }
              formik.setFieldValue("type", e.target.value);
            }}
            value={formik.values.type}
            className="h-full w-full rounded-md border border-gray-300 p-2"
          >
            {deleveryTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Table Select Modal */}
        {formik.values.type === "internal" && (
          <CashierTableList
            selectedTable={selectedTable}
            setSelectedTable={setselectedTable}
          />
        )}

        {/* Customer Data */}
        <div className="flex flex-col gap-y-2 rounded-sm bg-gray-200 px-3 py-2 sm:flex-row sm:justify-between">
          <p className="font-semibold">
            {t("cashierPaymentMethod.customerName")}{" "}
            <span className="font-normal">
              {customerData.name || t("cashierPaymentMethod.unknown")}
            </span>
          </p>
          <p className="font-semibold">
            {t("cashierPaymentMethod.customerPhone")}
            <span className="font-normal">
              {customerData.phone || t("cashierPaymentMethod.unknown")}
            </span>
          </p>
        </div>

        <button
          disabled={isRequesting}
          onClick={() => formik.handleSubmit()}
          className="w-full rounded-md bg-sidebar py-2 text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-600"
        >
          {paymentType === "pay-later" ? t("cashierPaymentMethod.submit") : t("cashierPaymentMethod.pay")}
        </button>
      </div>
    </div>
  );
}

export default CashierPaymentModal;
